'use client'

import { Control, Controller, FieldError, FieldValues, Path } from 'react-hook-form'

import { emailRules, emailRulesOptional, normalizeEmail } from '@/shared/lib/validation/email'
import { Input, InputProps } from '@/shared/ui/Input'

/**
 * React Hook Form Email Input component
 * Включает автоматическую валидацию и нормализацию email
 *
 * @example
 * ```tsx
 * // Обязательный email
 * <RHFEmailInput
 *   name="EMAIL"
 *   control={control}
 *   error={errors.EMAIL}
 * />
 *
 * // Необязательный email
 * <RHFEmailInput
 *   name="EMAIL"
 *   control={control}
 *   error={errors.EMAIL}
 *   required={false}
 * />
 * ```
 */

export interface RHFEmailInputProps<T extends FieldValues> extends Omit<InputProps, 'name' | 'type'> {
	/** Имя поля в react-hook-form */
	name: Path<T>
	/** Control из react-hook-form */
	control: Control<T>
	/** Ошибка валидации из react-hook-form */
	error?: FieldError
	/** Кастомный label, по умолчанию "E-mail" */
	label?: string
	/** Обязательное ли поле, по умолчанию true */
	required?: boolean
}

export const RHFEmailInput = <T extends FieldValues>({
	name,
	control,
	error,
	label = 'E-mail',
	required = true,
	...inputProps
}: RHFEmailInputProps<T>) => {
	const validationRules = required ? emailRules : emailRulesOptional

	return (
		<Controller
			name={name}
			control={control}
			rules={validationRules}
			render={({ field, fieldState }) => {
				const currentError = fieldState.error || error

				return (
					<Input
						{...field}
						{...inputProps}
						label={label}
						type="text"
						inputMode="email"
						autoComplete="email"
						isError={!!currentError}
						helperText={currentError?.message}
						onChange={field.onChange}
						onClear={() => field.onChange('')}
						onBlur={e => {
							field.onChange(normalizeEmail(e.target.value))
							e.target.value = normalizeEmail(e.target.value)
							field.onBlur()
						}}
					/>
				)
			}}
		/>
	)
}
